import { useEffect, useMemo, useState } from "react";
import {
  Loader2, Building2, Users, History, Settings, ShieldCheck, Palette,
  AtSign, SlidersHorizontal, LayoutDashboard, Mail, UserPlus,
} from "lucide-react";
import { WorkspacePanel } from "../WorkspacePanel";
import { WorkspaceDirectory } from "../WorkspaceDirectory";
import { HistoryPanel } from "../HistoryPanel";
import { AdministrationPanel } from "../AdministrationPanel";
import { PersonalizationPanel } from "../PersonalizationPanel";
import { SubsectionTabs } from "./SubsectionTabs";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const META = {
  workspace: { title: "Mon Workspace", subtitle: "Équipe, invitations, sièges et organisation du Workspace actif.", icon: Building2 },
  directory: { title: "Annuaire", subtitle: "Contacts sécurisés du Workspace actif.", icon: Users },
  history: { title: "Historique", subtitle: "Journal des opérations et événements accessibles dans ce Workspace.", icon: History },
  administration: { title: "Administration", subtitle: "Paramètres de sécurité, identité et configuration du Workspace.", icon: Settings },
};

const ADMIN_TABS = [
  { key: "security", label: "Sécurité", path: "/compte/administration/securite", icon: ShieldCheck },
  { key: "branding", label: "Personnalisation", path: "/compte/administration/personnalisation", icon: Palette },
  { key: "domains", label: "Domaines & e-mails", path: "/compte/administration/domaines", icon: AtSign },
  { key: "advanced", label: "Avancé", path: "/compte/administration/avance", icon: SlidersHorizontal },
];

const WORKSPACE_TABS = [
  { key: "overview", label: "Vue générale", path: "/compte/workspace/apercu", icon: LayoutDashboard },
  { key: "team", label: "Équipe", path: "/compte/workspace/equipe", icon: Users },
  { key: "invitations", label: "Invitations", path: "/compte/workspace/invitations", icon: Mail, managerOnly: true },
  { key: "join", label: "Adhésions", path: "/compte/workspace/adhesions", icon: UserPlus },
];

const WORKSPACE_COPY = {
  overview: "Vue d’ensemble du Workspace actif, sièges et référence publique.",
  team: "Membres, rôles, statut et activité de l’équipe.",
  invitations: "Invitations en attente et demandes d’adhésion reçues.",
  join: "Rechercher un Workspace, demander à le rejoindre et suivre vos demandes.",
};

export const WorkspaceSectionPage = ({ section, adminView = "security", workspaceView = "overview" }) => {
  const { activeWorkspaceId, activeWorkspace } = useAuth();
  const [sub, setSub] = useState(null);
  const [loadingSub, setLoadingSub] = useState(section === "administration");
  const role = activeWorkspace?.role || "member";
  const meta = META[section] || META.workspace;
  const Icon = meta.icon;
  const canManageWorkspace = role === "owner" || role === "admin";

  const workspaceTabs = useMemo(
    () => WORKSPACE_TABS.filter((item) => !item.managerOnly || canManageWorkspace),
    [canManageWorkspace],
  );

  useEffect(() => {
    let active = true;
    if (section !== "administration") {
      setLoadingSub(false);
      return () => { active = false; };
    }
    setLoadingSub(true);
    api.get("/account/subscription")
      .then(({ data }) => { if (active) setSub(data); })
      .catch(() => { if (active) setSub(null); })
      .finally(() => { if (active) setLoadingSub(false); });
    return () => { active = false; };
  }, [section, activeWorkspaceId]);

  const forbidden =
    (section === "directory" && role === "billing_admin") ||
    (section === "workspace" && role === "billing_admin") ||
    (section === "workspace" && workspaceView === "invitations" && !canManageWorkspace) ||
    (section === "administration" && !(role === "owner" || role === "admin"));

  if (forbidden) {
    return (
      <div className="max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <h1 className="font-heading text-2xl font-bold">Accès non disponible</h1>
        <p className="text-slate-500 mt-2">Votre rôle dans ce Workspace ne donne pas accès à cette rubrique.</p>
      </div>
    );
  }

  const renderAdministration = () => {
    if (loadingSub) {
      return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
    }

    if (adminView === "branding") {
      return (
        <div className="personalization-view-branding">
          <PersonalizationPanel workspaceId={activeWorkspaceId} role={role} entitlements={sub?.entitlements || {}} />
        </div>
      );
    }

    if (adminView === "domains") {
      return (
        <div className="personalization-view-domains">
          <PersonalizationPanel workspaceId={activeWorkspaceId} role={role} entitlements={sub?.entitlements || {}} />
        </div>
      );
    }

    if (adminView === "advanced") {
      return (
        <div className="admin-view-advanced">
          <AdministrationPanel workspaceId={activeWorkspaceId} role={role} plan={sub?.plan} />
        </div>
      );
    }

    return (
      <div className="admin-view-security">
        <AdministrationPanel workspaceId={activeWorkspaceId} role={role} plan={sub?.plan} />
      </div>
    );
  };

  const subtitle = section === "workspace"
    ? (WORKSPACE_COPY[workspaceView] || WORKSPACE_COPY.overview)
    : meta.subtitle;

  return (
    <div data-testid={`app-section-${section}`} className="space-y-6">
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-1"><Icon className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-[0.16em]">Workspace</span></div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">{meta.title}</h1>
        <p className="text-slate-500 mt-2">{subtitle}</p>
      </div>

      {section === "workspace" && <SubsectionTabs items={workspaceTabs} active={workspaceView} />}
      {section === "administration" && <SubsectionTabs items={ADMIN_TABS} active={adminView} />}

      {section === "workspace" && (
        <div className={`workspace-view-${workspaceView}`}>
          <WorkspacePanel workspaceId={activeWorkspaceId} role={role} />
        </div>
      )}
      {section === "directory" && <WorkspaceDirectory role={role} />}
      {section === "history" && <HistoryPanel workspaceId={activeWorkspaceId} role={role} />}
      {section === "administration" && renderAdministration()}
    </div>
  );
};
